import React, { useEffect, Suspense, lazy } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import "./App.css";
import Layout from "./layout/layout";
import { jwtDecode } from "jwt-decode";
import { generateToken, messaging } from "./notifications/firebase";
import { onMessage } from "firebase/messaging";
import toast, { Toaster } from "react-hot-toast";
import { CircularProgress, Box } from "@mui/material";
import { useDispatch, useSelector } from 'react-redux';
import { fetchCurrentUser, selectToken } from './store/slices/authSlice';
import PermissionGuard from './components/common/PermissionGuard';
import PERMISSIONS from './lib/permissions';
import './i18n';
import { useTranslation } from 'react-i18next';

// ⚡ LAZY LOADING - Load components only when needed
// Keep Login eagerly loaded (first page users see)
import Login from "./pages/dashboard/login";

// Lazy load all dashboard components
const Dashboard = lazy(() => import("./pages/dashboard/Dashboard"));
const Maintenance = lazy(() => import("./pages/dashboard/Maintenance/Maintenance"));
const Users = lazy(() => import("./pages/dashboard/Users/Users"));
const Analysis = lazy(() => import("./pages/dashboard/Analysis/Analysis"));
const Owners = lazy(() => import("./pages/dashboard/Owners/Owners"));
const Requests = lazy(() => import("./pages/dashboard/Requests/Requests"));
const Units = lazy(() => import("./pages/dashboard/Units/Units"));
const Payment = lazy(() => import("./pages/dashboard/Payment/Payment"));
const ContactUs = lazy(() => import("./pages/dashboard/ContactUs/ContactUs"));
const ChangePassword = lazy(() => import("./pages/dashboard/ChangePassword/ChangePawword"));
const Profile = lazy(() => import("./pages/dashboard/Profile/Profile"));
const User = lazy(() => import("./pages/dashboard/Users/user"));
const Booking = lazy(() => import("./pages/dashboard/booking/booking"));
const Contracts = lazy(() => import("./pages/dashboard/contract/Contracts"));
const Servicerequest = lazy(() => import("./pages/dashboard/servicerequest/servicerequest"));
const Unit = lazy(() => import("./pages/dashboard/Units/unit"));
const Brokers = lazy(() => import("./pages/dashboard/Brokers/brokers"));
const BookingId = lazy(() => import("./pages/dashboard/booking/BookingId"));
const Maintenanceservice = lazy(() => import("./pages/dashboard/MaintenanceService/Maintenanceservice"));
const CancelRequest = lazy(() => import("./pages/dashboard/CancelRequest/CancelRequest"));
const Refundrequest = lazy(() => import("./pages/dashboard/RefundRequest/refundrequest"));
const Banks = lazy(() => import("./pages/dashboard/Banks/banks"));
const GetBanksAccount = lazy(() => import("./pages/dashboard/BankAccount/GetBanksAccount"));
const Service = lazy(() => import("./pages/dashboard/Service/Service"));
const PromoCode = lazy(() => import("./pages/dashboard/PromoCode/PromoCode"));
const SocialMedia = lazy(() => import("./pages/dashboard/SocialMedia/SocialMedia"));
const Setting = lazy(() => import("./pages/dashboard/Setting/Setting"));
const Chats = lazy(() => import("./pages/dashboard/Chats/Chats"));
const Settingtype = lazy(() => import("./pages/dashboard/Settingtype/Settingtype"));
const Brokerpayment = lazy(() => import("./pages/dashboard/Brokerpayment/brokerpayment"));
const Notification = lazy(() => import("./pages/dashboard/Notification/Notification"));
const Features = lazy(() => import("./pages/dashboard/features/features"));
const Compounds = lazy(() => import("./pages/dashboard/compounds/compounds"));
const Unittypes = lazy(() => import("./pages/dashboard/unittypes/unittypes"));
const Placetype = lazy(() => import("./pages/dashboard/placetype/placetype"));
const Identitytypes = lazy(() => import("./pages/dashboard/identitytypes/identitytypes"));
const Unitprices = lazy(() => import("./pages/dashboard/unitprices/unitprices"));
const Country = lazy(() => import("./pages/dashboard/country/country"));
const Cites = lazy(() => import("./pages/dashboard/cities/cites"));
const GetBankOwners = lazy(() => import("./pages/dashboard/BankOwners/GetBankOwners"));
const Paymentmethods = lazy(() => import("./pages/dashboard/paymentmethod/paymentmethods"));
const AddUnit = lazy(() => import("./pages/dashboard/AddUnit/AddUnit"));
const AdminUserForm = lazy(() => import("./pages/dashboard/Users/AdminUserForm"));
const Admins = lazy(() => import("./pages/dashboard/Admins/Admins"));
const RolesPermissions = lazy(() => import("./pages/dashboard/RolesPermissions/RolesPermissions"));
const SettingsPage = lazy(() => import("./pages/dashboard/Settings/Settings"));

// 🎯 LOADING COMPONENT - Professional loading spinner
const PageLoader = () => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '60vh',
        flexDirection: 'column',
        gap: 2
      }}
    >
      <CircularProgress size={50} sx={{ color: '#60A9C6' }} />
      <Box sx={{ textAlign: 'center', color: '#666' }}>
        {t('common.loading')}
      </Box>
    </Box>
  );
};

// 🛡️ LAZY ROUTE WRAPPER - Wraps components with loading state
const LazyRoute = ({ children }) => (
  <Suspense fallback={<PageLoader />}>
    {children}
  </Suspense>
);

// 🚫 PERMISSION DENIED COMPONENT
const PermissionDenied = () => {
  const { t } = useTranslation();
  return <div style={{padding:16}}>{t('common.error')}</div>;
};

const checkTokenExpiration = async (navigate) => {
  const currentPath = window.location.pathname;

  // Skip token check for login page
  if (currentPath === "/") return;

  const token = localStorage.getItem("token");

  if (!token) {
    navigate("/");
    return;
  }

  try {
    const decodedToken = jwtDecode(token);
    const expirationTime = decodedToken.exp * 1000; // Token expiration time in milliseconds
    const currentTime = Date.now();
    const timeRemaining = expirationTime - currentTime;

    // If token is already expired, redirect to login
    if (timeRemaining <= 0) {
      console.log("Token expired, redirecting to login");
      localStorage.removeItem("token");
      localStorage.removeItem("refreshtoken");
      navigate("/");
      return;
    }

    // Token is valid - axios interceptor will handle refresh on 401 errors
    console.log(`Token valid for ${Math.round(timeRemaining / 1000 / 60)} more minutes`);
  } catch (error) {
    console.error("Invalid token format:", error);
    localStorage.removeItem("token");
    localStorage.removeItem("refreshtoken");
    navigate("/");
  }
};

function App() {
  const navigate = useNavigate(); // استخدام useNavigate للتنقل بين الصفحات
  const dispatch = useDispatch();
  const token = useSelector(selectToken);
  const { i18n } = useTranslation();

  useEffect(() => {
    checkTokenExpiration(navigate);
  }, [navigate]);

  useEffect((params) => {
    generateToken();
    onMessage(messaging, (payload) => {
      console.log(payload);
      toast(payload.notification.body);
    });
  }, []);

  useEffect(() => {
    // hydrate current user and permissions whenever token exists/changes
    if (token) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch, token]);

  useEffect(() => {
    // Set document direction based on language
    const currentLanguage = i18n.language;
    if (currentLanguage === 'ar') {
      document.documentElement.dir = 'rtl';
      document.documentElement.lang = 'ar';
    } else {
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = 'en';
    }
  }, [i18n.language]);

  return (
    <>
      <Toaster position="top-right" />

      <Routes>
        {/* Login page - loaded eagerly (first page) */}
        <Route path="/" element={<Login />} />
        
        {/* ⚡ All dashboard routes with lazy loading */}
        <Route path="dashboard" element={<Layout children={<LazyRoute><Dashboard /></LazyRoute>} />} />
        <Route path="maintenance" element={
          <PermissionGuard required={[PERMISSIONS.MAINTENANCE.LIST]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><Maintenance /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="maintenanceservice" element={<Layout children={<LazyRoute><Maintenanceservice /></LazyRoute>} />} />
        <Route path="users" element={
          <PermissionGuard required={[PERMISSIONS.CLIENT.LIST]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><Users /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="admins" element={
          <PermissionGuard required={[PERMISSIONS.ROLE.LIST]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><Admins /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="roles" element={
          <PermissionGuard required={[PERMISSIONS.ROLE.LIST]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><RolesPermissions /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="users/create" element={
          <PermissionGuard required={[PERMISSIONS.CLIENT.CREATE]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><AdminUserForm mode="create" /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="users/:id/edit" element={
          <PermissionGuard required={[PERMISSIONS.CLIENT.UPDATE]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><AdminUserForm mode="edit" /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="analysis" element={<Layout children={<LazyRoute><Analysis /></LazyRoute>} />} />
        <Route path="owners" element={
          <PermissionGuard required={[PERMISSIONS.OWNER.LIST]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><Owners /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="requests" element={<Layout children={<LazyRoute><Requests /></LazyRoute>} />} />
        <Route path="units" element={
          <PermissionGuard required={[PERMISSIONS.UNIT.LIST]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><Units /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="cancelrequest" element={
          <PermissionGuard required={[PERMISSIONS.CANCELLATION_REQUEST.LIST]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><CancelRequest /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="refundrequest" element={
          <PermissionGuard required={[PERMISSIONS.REFUND_REQUEST.LIST]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><Refundrequest /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="bankowners" element={
          <PermissionGuard required={[PERMISSIONS.BANK_ACCOUNT.LIST]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><GetBankOwners /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="banks" element={<Layout children={<LazyRoute><Banks /></LazyRoute>} />} />
        <Route path="features" element={<Layout children={<LazyRoute><Features /></LazyRoute>} />} />
        <Route path="country" element={<Layout children={<LazyRoute><Country /></LazyRoute>} />} />
        <Route path="cites" element={<Layout children={<LazyRoute><Cites /></LazyRoute>} />} />
        <Route path="placetype" element={<Layout children={<LazyRoute><Placetype /></LazyRoute>} />} />
        <Route path="identitytypes" element={<Layout children={<LazyRoute><Identitytypes /></LazyRoute>} />} />
        <Route path="compounds" element={<Layout children={<LazyRoute><Compounds /></LazyRoute>} />} />
        <Route path="unittypes" element={<Layout children={<LazyRoute><Unittypes /></LazyRoute>} />} />
        <Route path="banksaccount" element={<Layout children={<LazyRoute><GetBanksAccount /></LazyRoute>} />} />
        <Route path="insurancerefund" element={<Layout children={<LazyRoute><Refundrequest /></LazyRoute>} />} />
        <Route path="paymentmethods" element={<Layout children={<LazyRoute><Paymentmethods /></LazyRoute>} />} />
        
        {/* Dynamic routes */}
        <Route path="unit/:id" element={
          <PermissionGuard required={[PERMISSIONS.UNIT.SHOW]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><Unit /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="booking/:id" element={<Layout children={<LazyRoute><BookingId /></LazyRoute>} />} />
        
        {/* Other pages */}
        <Route path="payment" element={<Layout children={<LazyRoute><Payment /></LazyRoute>} />} />
        <Route path="contactus" element={
          <PermissionGuard required={[PERMISSIONS.CONTACT_US.LIST]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><ContactUs /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="changepassword" element={<Layout children={<LazyRoute><ChangePassword /></LazyRoute>} />} />
        <Route path="profile" element={<Layout children={<LazyRoute><Profile /></LazyRoute>} />} />
        <Route path="user" element={<Layout children={<LazyRoute><User /></LazyRoute>} />} />
        <Route path="brokers" element={
          <PermissionGuard required={[PERMISSIONS.BROKER.LIST]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><Brokers /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="booking" element={<Layout children={<LazyRoute><Booking /></LazyRoute>} />} />
        <Route path="contract" element={
          <PermissionGuard required={[PERMISSIONS.CONTRACT.LIST]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><Contracts /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="servicereq" element={
          <PermissionGuard required={[PERMISSIONS.SERVICE_REQUEST.LIST]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><Servicerequest /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="services" element={<Layout children={<LazyRoute><Service /></LazyRoute>} />} />
        <Route path="promo" element={<Layout children={<LazyRoute><PromoCode /></LazyRoute>} />} />
        <Route path="socialmedia" element={<Layout children={<LazyRoute><SocialMedia /></LazyRoute>} />} />
        <Route path="setting" element={
          <PermissionGuard required={[PERMISSIONS.SETTING.LIST]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><Setting /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="unitprices" element={<Layout children={<LazyRoute><Unitprices /></LazyRoute>} />} />
        <Route path="settingtype" element={
          <PermissionGuard required={[PERMISSIONS.SETTING_TYPE.LIST]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><Settingtype /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="settings" element={
          <PermissionGuard 
            required={[
              PERMISSIONS.SETTING.LIST,
              PERMISSIONS.BANK.LIST,
              PERMISSIONS.COMPOUND.LIST,
              PERMISSIONS.FEATURE.LIST,
              PERMISSIONS.SERVICE.LIST,
              PERMISSIONS.COUNTRY.LIST,
              PERMISSIONS.CITY.LIST,
              PERMISSIONS.UNIT_TYPE.LIST,
              PERMISSIONS.PLACE_TYPE.LIST,
              PERMISSIONS.IDENTITY_TYPE.LIST,
              PERMISSIONS.PAYMENT_METHOD.LIST,
              PERMISSIONS.PROMO_CODE.LIST,
              PERMISSIONS.MAINTENANCE_SERVICE.LIST,
              PERMISSIONS.SOCIAL_MEDIA.LIST,
              PERMISSIONS.SETTING_TYPE.LIST
            ]} 
            fallback={<PermissionDenied />}
            requireAll={false}
          >
            <Layout children={<LazyRoute><SettingsPage /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="notification" element={<Layout children={<LazyRoute><Notification /></LazyRoute>} />} />
        <Route path="chats" element={
          <PermissionGuard required={[PERMISSIONS.CHAT.LIST]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><Chats /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="brokerpayment" element={
          <PermissionGuard required={[PERMISSIONS.BROKER_PAYMENT.LIST]} fallback={<PermissionDenied />}>
            <Layout children={<LazyRoute><Brokerpayment /></LazyRoute>} />
          </PermissionGuard>
        } />
        <Route path="units/add" element={<Layout children={<LazyRoute><AddUnit /></LazyRoute>} />} />
      </Routes>
    </>
  );
}

export default App;
